import type { ReactElement } from 'react';

/**
 * Drawn scenes for slides with no photograph yet.
 *
 * <p>The catalogue has no product or lifestyle photography (neither supplier feed carries
 * images), and a grey box with "image coming soon" on the first thing a visitor sees says the
 * shop is unfinished. A simple drawing in the shop's own colours says nothing false and gives the
 * slide its shape. An operator replaces it by giving the slide an `image`.
 *
 * <p>Every fill is a CSS class read from the theme's tokens (see `HOME_CSS`), not a hex in the
 * markup — a presentation attribute cannot take `var()`, and a colour written here would not
 * follow the shop when it is recoloured.
 */
export type SceneArt = 'roof' | 'inverter' | 'pallets';

export const SCENE_ARTS: readonly SceneArt[] = ['roof', 'inverter', 'pallets'];

export function Scene({ art }: { art: SceneArt }) {
  return (
    <svg
      className="ae-scene"
      viewBox="0 0 1400 440"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <rect className="ae-scene__sky" width="1400" height="440" />
      {art === 'roof' ? <Roof /> : art === 'inverter' ? <Inverter /> : <Pallets />}
    </svg>
  );
}

function Roof() {
  const top = [915, 971, 1027, 1083];
  const bottom = [880, 940, 1000, 1060, 1120, 1180];
  return (
    <g>
      <circle className="ae-scene__sun-glow" cx="1250" cy="92" r="84" />
      <circle className="ae-scene__sun" cx="1250" cy="92" r="54" />
      <path className="ae-scene__ground" d="M600 380 Q900 330 1400 360 V440 H600z" />
      <rect className="ae-scene__wall" x="830" y="258" width="430" height="182" />
      <rect className="ae-scene__window" x="900" y="300" width="70" height="60" />
      <rect className="ae-scene__window" x="1120" y="300" width="70" height="60" />
      <rect className="ae-scene__door" x="1010" y="340" width="70" height="100" />
      <polygon className="ae-scene__roof" points="790,262 900,140 1190,140 1300,262" />
      {top.map((x) => (
        <rect key={`t${x}`} className="ae-scene__panel" x={x} y="152" width="52" height="42" />
      ))}
      {bottom.map((x) => (
        <rect key={`b${x}`} className="ae-scene__panel" x={x} y="200" width="56" height="48" />
      ))}
    </g>
  );
}

function Inverter() {
  return (
    <g>
      <rect className="ae-scene__ground" x="620" y="360" width="780" height="80" />
      <rect className="ae-scene__window" x="1150" y="70" width="190" height="130" />
      {[1165, 1220, 1275].map((x) => (
        <rect key={x} className="ae-scene__panel" x={x} y="85" width="50" height="40" />
      ))}
      <rect className="ae-scene__unit" x="820" y="110" width="150" height="190" rx="12" />
      <rect className="ae-scene__screen" x="850" y="140" width="90" height="46" rx="4" />
      <circle className="ae-scene__led" cx="870" cy="230" r="6" />
      <rect className="ae-scene__unit" x="1010" y="170" width="120" height="190" rx="10" />
      <path className="ae-scene__line" d="M1010 233h120M1010 296h120" />
      <path className="ae-scene__bolt" d="M1075 190l-12 22h16l-12 22" />
      <path className="ae-scene__cable" d="M895 300v40h175v-40" />
    </g>
  );
}

function Pallets() {
  return (
    <g>
      <rect className="ae-scene__ground" x="620" y="370" width="780" height="70" />
      <rect className="ae-scene__pallet" x="780" y="352" width="200" height="18" />
      <rect className="ae-scene__stack" x="785" y="190" width="190" height="162" />
      <path className="ae-scene__line" d="M785 222h190M785 254h190M785 286h190M785 318h190" />
      <rect className="ae-scene__pallet" x="1000" y="352" width="200" height="18" />
      <rect className="ae-scene__stack" x="1005" y="240" width="190" height="112" />
      <path className="ae-scene__line" d="M1005 268h190M1005 296h190M1005 324h190" />
      <rect className="ae-scene__unit" x="1220" y="300" width="150" height="70" rx="8" />
      <rect className="ae-scene__unit" x="1230" y="250" width="100" height="56" rx="6" />
      <circle className="ae-scene__wheel" cx="1255" cy="372" r="16" />
      <circle className="ae-scene__wheel" cx="1340" cy="372" r="16" />
    </g>
  );
}

/**
 * The Horizon hero's pre-dawn panel field (redesign 2026-10-01, DESIGN.md §6).
 *
 * <p>The geometry is the mockup's `.hero-art` SVG exactly: a gradient sky, the first-light
 * glow on the horizon, and two rows of panel trapezoids catching it. Every paint comes from a
 * CSS class defined in `SHOP_INTRO_CSS`, reading the `--ae-hero-*` scope — never a hex here.
 * The panel fills and frame strokes carry their opacity from tokens too, because the Day
 * palette does not just recolour them, it reweights them (near-opaque light slate instead of
 * translucent navy — the owner rejected dark panel squares on a light sky). Fixed stop
 * opacities (the glow, ember, sheen) are the composition itself and stay as attributes.
 */
export function DawnScape() {
  return (
    <svg
      className="ae-dawn"
      viewBox="0 0 1440 760"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="ae-dawn-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="ae-dawn__sky-top" />
          <stop offset="0.62" className="ae-dawn__sky-mid" />
          <stop offset="1" className="ae-dawn__sky-low" />
        </linearGradient>
        <radialGradient id="ae-dawn-dawn" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" className="ae-dawn__glow" stopOpacity="0.38" />
          <stop offset="0.45" className="ae-dawn__ember" stopOpacity="0.10" />
          <stop offset="1" className="ae-dawn__ember" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ae-dawn-core" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" className="ae-dawn__core" stopOpacity="0.5" />
          <stop offset="1" className="ae-dawn__glow" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="ae-dawn-horizon" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" className="ae-dawn__glow" stopOpacity="0" />
          <stop offset="0.52" className="ae-dawn__glow" stopOpacity="0.26" />
          <stop offset="0.625" className="ae-dawn__glow" stopOpacity="0.95" />
          <stop offset="0.75" className="ae-dawn__glow" stopOpacity="0.26" />
          <stop offset="1" className="ae-dawn__glow" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ae-dawn-panel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" className="ae-dawn__panel-hi" />
          <stop offset="1" className="ae-dawn__panel-lo" />
        </linearGradient>
        <linearGradient id="ae-dawn-catch" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" className="ae-dawn__glow" stopOpacity="0" />
          <stop offset="0.5" className="ae-dawn__glow" stopOpacity="0.55" />
          <stop offset="1" className="ae-dawn__glow" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="ae-dawn-sheen" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0.2" className="ae-dawn__sheen" stopOpacity="0" />
          <stop offset="0.5" className="ae-dawn__sheen" stopOpacity="0.03" />
          <stop offset="0.8" className="ae-dawn__sheen" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="1440" height="760" fill="url(#ae-dawn-sky)" />
      <rect width="1440" height="760" fill="url(#ae-dawn-sheen)" />
      <ellipse cx="900" cy="575" rx="480" ry="300" fill="url(#ae-dawn-dawn)" />
      <ellipse cx="900" cy="562" rx="150" ry="60" fill="url(#ae-dawn-core)" />
      <line x1="0" y1="560" x2="1440" y2="560" stroke="url(#ae-dawn-horizon)" strokeWidth="1.5" />
      <g className="ae-dawn__array ae-dawn__array--far" fill="url(#ae-dawn-panel)" strokeWidth="1">
        <path d="M 120 598 L 236 598 L 218 566 L 112 566 Z" />
        <path d="M 268 598 L 384 598 L 366 566 L 260 566 Z" />
        <path d="M 416 598 L 532 598 L 514 566 L 408 566 Z" />
        <path d="M 564 598 L 680 598 L 662 566 L 556 566 Z" />
        <path d="M 712 598 L 828 598 L 810 566 L 704 566 Z" />
        <path d="M 860 598 L 976 598 L 958 566 L 852 566 Z" />
        <path d="M 1008 598 L 1124 598 L 1106 566 L 1000 566 Z" />
        <path d="M 1156 598 L 1272 598 L 1254 566 L 1148 566 Z" />
        <path d="M 1304 598 L 1420 598 L 1402 566 L 1296 566 Z" />
      </g>
      <g stroke="url(#ae-dawn-catch)" strokeWidth="1.6" fill="none">
        <path d="M 704 566 L 810 566" />
        <path d="M 852 566 L 958 566" />
        <path d="M 1000 566 L 1106 566" />
      </g>
      <g className="ae-dawn__array ae-dawn__array--near" fill="url(#ae-dawn-panel)" strokeWidth="1">
        <path d="M 24 724 L 262 724 L 222 626 L 48 626 Z" />
        <path d="M 310 724 L 548 724 L 508 626 L 334 626 Z" />
        <path d="M 596 724 L 834 724 L 794 626 L 620 626 Z" />
        <path d="M 882 724 L 1120 724 L 1080 626 L 906 626 Z" />
        <path d="M 1168 724 L 1406 724 L 1366 626 L 1192 626 Z" />
      </g>
      <g stroke="url(#ae-dawn-catch)" strokeWidth="2" fill="none">
        <path d="M 620 626 L 794 626" />
        <path d="M 906 626 L 1080 626" />
      </g>
      <g className="ae-dawn__rails" strokeWidth="1" fill="none">
        <path d="M 678 724 L 650 626 M 755 724 L 727 626" />
        <path d="M 964 724 L 936 626 M 1041 724 L 1013 626" />
        <path d="M 608 675 L 814 675 M 894 675 L 1100 675" />
      </g>
    </svg>
  );
}

/** Line icons for the category tiles, drawn in the same hand as the scenes. */
export type TileIcon =
  | 'panel'
  | 'inverter'
  | 'battery'
  | 'mounting'
  | 'ev'
  | 'cable'
  | 'switch'
  | 'protection'
  | 'accessory'
  | 'kit';

export const TILE_ICONS: readonly TileIcon[] = [
  'panel', 'inverter', 'battery', 'mounting', 'ev', 'cable', 'switch', 'protection', 'accessory', 'kit',
];

const ICON_PATHS: Record<TileIcon, ReactElement> = {
  panel: (
    <>
      <rect className="ae-icon__fill" x="10" y="6" width="44" height="52" rx="2" />
      <path d="M10 19h44M10 32h44M10 45h44M24.7 6v52M39.3 6v52" />
    </>
  ),
  inverter: (
    <>
      <rect className="ae-icon__fill" x="14" y="8" width="36" height="46" rx="4" />
      <rect x="22" y="16" width="20" height="10" rx="1" />
      <circle cx="26" cy="40" r="2" />
      <circle cx="38" cy="40" r="2" />
      <path d="M22 58v-4M42 58v-4" />
    </>
  ),
  battery: (
    <>
      <rect className="ae-icon__fill" x="16" y="6" width="32" height="52" rx="4" />
      <path d="M34 20l-6 12h8l-6 12" />
    </>
  ),
  mounting: (
    <>
      <path d="M6 44l26-20 26 20" />
      <path d="M14 38h36M14 30h36" strokeWidth="3" />
      <path d="M20 38v8M44 38v8" />
    </>
  ),
  ev: (
    <>
      <rect className="ae-icon__fill" x="16" y="8" width="24" height="44" rx="4" />
      <path d="M40 28h6a4 4 0 014 4v12a3 3 0 006 0V24" />
      <path d="M30 20l-4 8h6l-4 8" />
    </>
  ),
  cable: (
    <>
      <circle className="ae-icon__fill" cx="32" cy="32" r="20" />
      <circle cx="32" cy="32" r="8" />
      <path d="M52 32h8" />
    </>
  ),
  switch: (
    <>
      <rect className="ae-icon__fill" x="14" y="10" width="36" height="44" rx="3" />
      <rect x="26" y="22" width="12" height="20" rx="2" />
      <path d="M32 26v8" />
    </>
  ),
  protection: (
    <>
      <path className="ae-icon__fill" d="M32 6l20 8v14c0 14-9 22-20 26C21 50 12 42 12 28V14z" />
      <path d="M34 18l-6 12h8l-6 12" />
    </>
  ),
  accessory: (
    <>
      <circle className="ae-icon__fill" cx="32" cy="32" r="8" />
      <path d="M32 10v8M32 46v8M10 32h8M46 32h8M16 16l6 6M42 42l6 6M16 48l6-6M42 22l6-6" />
    </>
  ),
  kit: (
    <>
      <rect className="ae-icon__fill" x="6" y="10" width="30" height="24" rx="2" />
      <path d="M6 22h30M21 10v24" />
      <rect className="ae-icon__fill" x="42" y="24" width="16" height="22" rx="3" />
      <path d="M36 34h6M20 34v14h22" />
    </>
  ),
};

export function TileGlyph({ icon }: { icon: TileIcon }) {
  return (
    <svg className="ae-icon" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      {ICON_PATHS[icon]}
    </svg>
  );
}
